if (typeof FlourishProduct !== 'function') {
  class FlourishProduct extends HTMLElement {
    connectedCallback() {
      this._track = this.querySelector('[data-flourish-product-track]');
      this._slides = this.querySelectorAll('[data-flourish-product-slide]');
      this._thumbs = this.querySelectorAll('[data-flourish-product-thumb]');
      this._mobileQuery = window.matchMedia('(max-width: 1023px)');
      this._preloadedUrls = new Set();

      this._onThumbClick = this._onThumbClick.bind(this);
      this._onThumbHover = this._onThumbHover.bind(this);
      this._onTrackScroll = this._onTrackScroll.bind(this);
      this._onMediaChange = this._onMediaChange.bind(this);

      this._thumbs.forEach((thumb) => {
        thumb.addEventListener('click', this._onThumbClick);
        thumb.addEventListener('mouseenter', this._onThumbHover);
      });

      this._track?.addEventListener('scroll', this._onTrackScroll, { passive: true });
      this._mobileQuery.addEventListener('change', this._onMediaChange);

      this._scheduleGalleryPreload();
    }

    disconnectedCallback() {
      this._thumbs?.forEach((thumb) => {
        thumb.removeEventListener('click', this._onThumbClick);
        thumb.removeEventListener('mouseenter', this._onThumbHover);
      });
      this._track?.removeEventListener('scroll', this._onTrackScroll);
      this._mobileQuery?.removeEventListener('change', this._onMediaChange);
    }

    _isMobileGallery() {
      return this._mobileQuery.matches;
    }

    _getPreloadWidth() {
      return this._isMobileGallery() ? 1000 : 1400;
    }

    _getSlideForMediaId(mediaId) {
      return Array.from(this._slides).find((slide) => slide.dataset.mediaId === mediaId);
    }

    _getPreloadSrc(slide) {
      if (!slide) return null;

      const datasetKey = this._isMobileGallery() ? 'preloadMobile' : 'preloadDesktop';
      const explicitSrc = slide.dataset[datasetKey];
      if (explicitSrc) return explicitSrc;

      const img = slide.querySelector('.flourish-product__main-img');
      if (!img) return null;

      return this._pickSrcFromSrcset(img.getAttribute('srcset'), this._getPreloadWidth()) || img.currentSrc || img.src;
    }

    _pickSrcFromSrcset(srcset, targetWidth) {
      if (!srcset) return null;

      const candidates = srcset
        .split(',')
        .map((part) => {
          const trimmed = part.trim();
          const spaceIndex = trimmed.lastIndexOf(' ');
          if (spaceIndex === -1) return null;

          const url = trimmed.slice(0, spaceIndex).trim();
          const width = parseInt(trimmed.slice(spaceIndex + 1), 10);
          return Number.isFinite(width) ? { url, width } : null;
        })
        .filter(Boolean)
        .sort((a, b) => a.width - b.width);

      if (!candidates.length) return null;

      const match = candidates.find((candidate) => candidate.width >= targetWidth);
      return (match || candidates[candidates.length - 1]).url;
    }

    _preloadSlide(slide, { priority = false } = {}) {
      const src = this._getPreloadSrc(slide);
      if (!src || this._preloadedUrls.has(src)) return;

      this._preloadedUrls.add(src);

      const preloadImage = new Image();
      preloadImage.decoding = 'async';
      if (priority && 'fetchPriority' in preloadImage) {
        preloadImage.fetchPriority = 'high';
      }
      preloadImage.src = src;
    }

    _getPreloadOrder() {
      const activeSlide = this.querySelector('[data-flourish-product-slide].is-active');
      const activeIndex = activeSlide ? Array.from(this._slides).indexOf(activeSlide) : 0;

      return Array.from(this._slides)
        .map((slide, index) => ({
          slide,
          distance: Math.abs(index - activeIndex),
        }))
        .sort((a, b) => a.distance - b.distance)
        .map((entry) => entry.slide);
    }

    _scheduleGalleryPreload() {
      const run = () => this._preloadGalleryImages();

      if ('requestIdleCallback' in window) {
        requestIdleCallback(run, { timeout: 1500 });
      } else {
        setTimeout(run, 150);
      }
    }

    _preloadGalleryImages() {
      const orderedSlides = this._getPreloadOrder();

      orderedSlides.forEach((slide, index) => {
        this._preloadSlide(slide, { priority: index === 0 });
      });
    }

    _setActiveMedia(mediaId, { scrollIntoView = false } = {}) {
      const slide = this._getSlideForMediaId(mediaId);
      if (!slide) return;

      this._slides.forEach((item) => {
        item.classList.toggle('is-active', item === slide);
      });

      this._thumbs.forEach((thumb) => {
        const isActive = thumb.dataset.mediaId === mediaId;
        thumb.classList.toggle('is-active', isActive);
        thumb.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });

      this._preloadSlide(slide, { priority: true });

      if (scrollIntoView && this._track) {
        this._track.scrollTo({
          left: slide.offsetLeft,
          behavior: 'smooth',
        });
      }
    }

    _getActiveSlideFromScroll() {
      if (!this._track || this._slides.length === 0) return null;

      const trackRect = this._track.getBoundingClientRect();
      const trackCenter = trackRect.left + trackRect.width / 2;

      let closestSlide = this._slides[0];
      let closestDistance = Number.POSITIVE_INFINITY;

      this._slides.forEach((slide) => {
        const slideRect = slide.getBoundingClientRect();
        const slideCenter = slideRect.left + slideRect.width / 2;
        const distance = Math.abs(slideCenter - trackCenter);

        if (distance < closestDistance) {
          closestDistance = distance;
          closestSlide = slide;
        }
      });

      return closestSlide;
    }

    _onThumbClick(event) {
      const button = event.currentTarget;
      const mediaId = button.dataset.mediaId;
      if (!mediaId) return;

      const slide = this._getSlideForMediaId(mediaId);
      if (slide) {
        this._preloadSlide(slide, { priority: true });
      }

      this._setActiveMedia(mediaId, { scrollIntoView: this._isMobileGallery() });
    }

    _onThumbHover(event) {
      const mediaId = event.currentTarget.dataset.mediaId;
      if (!mediaId) return;

      const slide = this._getSlideForMediaId(mediaId);
      if (slide) {
        this._preloadSlide(slide, { priority: true });
      }
    }

    _onTrackScroll() {
      if (!this._isMobileGallery()) return;

      const activeSlide = this._getActiveSlideFromScroll();
      if (!activeSlide?.dataset.mediaId) return;

      this._setActiveMedia(activeSlide.dataset.mediaId);

      const activeIndex = Array.from(this._slides).indexOf(activeSlide);
      const nextSlide = this._slides[activeIndex + 1];
      const previousSlide = this._slides[activeIndex - 1];

      if (nextSlide) this._preloadSlide(nextSlide);
      if (previousSlide) this._preloadSlide(previousSlide);
    }

    _onMediaChange() {
      this._preloadedUrls.clear();
      this._preloadGalleryImages();

      const activeSlide = this.querySelector('[data-flourish-product-slide].is-active');
      if (!activeSlide?.dataset.mediaId) return;

      if (this._isMobileGallery()) {
        this._setActiveMedia(activeSlide.dataset.mediaId, { scrollIntoView: false });
        return;
      }

      this._track?.scrollTo({ left: 0, behavior: 'auto' });
    }
  }

  if (typeof customElements.get('flourish-product') === 'undefined') {
    customElements.define('flourish-product', FlourishProduct);
  }
}

function initFlourishProductPriceSync() {
  document.querySelectorAll('.section-flourish-product').forEach((section) => {
    const mainPrice = section.querySelector('[data-update-block="flourish-price"]');
    const metaPrice = section.querySelector('[data-js-meta-price]');
    if (!mainPrice || !metaPrice) return;

    const syncMetaPrice = () => {
      metaPrice.innerHTML = mainPrice.innerHTML;
    };

    syncMetaPrice();
    new MutationObserver(syncMetaPrice).observe(mainPrice, {
      childList: true,
      subtree: true,
    });
  });
}

function initFlourishStickyAtc() {
  document.querySelectorAll('.flourish-product__sticky-bar').forEach((sticky) => {
    const productPage = document.getElementById(sticky.dataset.id);
    if (!productPage) return;

    const mainButton = productPage.querySelector('[data-js-product-add-to-cart]');
    const stickyPrice = sticky.querySelector('[data-js-atc-price]');
    const mainPrice = productPage.querySelector('[data-update-block="flourish-price"]');
    const stickyLabel = sticky.querySelector('[data-flourish-sticky-atc-label]');
    const mainLabel = productPage.querySelector('[data-js-product-add-to-cart-text]');

    const syncStickyHeight = () => {
      if (!sticky.classList.contains('visible')) {
        document.documentElement.style.removeProperty('--flourish-sticky-atc-height');
        return;
      }
      const height = Math.ceil(sticky.getBoundingClientRect().height);
      if (height > 0) {
        document.documentElement.style.setProperty('--flourish-sticky-atc-height', `${height}px`);
      }
    };

    const syncPrice = () => {
      if (!mainPrice || !stickyPrice) return;
      stickyPrice.innerHTML = `<span class="flourish-product__atc-prices">${mainPrice.innerHTML}</span>`;
    };

    const syncLabel = () => {
      if (!mainLabel || !stickyLabel) return;
      stickyLabel.textContent = mainLabel.textContent;
    };

    const syncButtonState = () => {
      if (!mainButton) return;

      sticky.querySelectorAll('[data-js-atc]').forEach((button) => {
        button.disabled = mainButton.disabled;
        button.classList.toggle('disabled', mainButton.classList.contains('disabled'));
        button.classList.toggle('working', mainButton.classList.contains('working'));
      });
    };

    syncPrice();
    syncLabel();
    syncButtonState();
    syncStickyHeight();

    if (mainPrice) {
      new MutationObserver(syncPrice).observe(mainPrice, {
        childList: true,
        subtree: true,
      });
    }

    if (mainLabel) {
      new MutationObserver(syncLabel).observe(mainLabel, {
        childList: true,
        characterData: true,
        subtree: true,
      });
    }

    if (mainButton) {
      new MutationObserver(syncButtonState).observe(mainButton, {
        attributes: true,
        attributeFilter: ['disabled', 'class'],
      });
    }

    new MutationObserver(syncStickyHeight).observe(sticky, {
      attributes: true,
      attributeFilter: ['class'],
    });
    window.addEventListener('resize', syncStickyHeight, { passive: true });

    const chooseButton = sticky.querySelector('[data-js-choose]');
    if (chooseButton) {
      chooseButton.addEventListener('click', () => {
        const target =
          productPage.querySelector('product-variants') ||
          productPage.querySelector('.flourish-product__shopify-variants') ||
          productPage.querySelector('.flourish-product__swatches');

        target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    }
  });
}

function initFlourishOpenCartOnAdd() {
  if (document.body.classList.contains('template-cart')) return;

  document.querySelectorAll('.section-flourish-product product-form[data-ajax-cart]').forEach((form) => {
    if (form.dataset.flourishCartDrawerBound === 'true') return;
    form.dataset.flourishCartDrawerBound = 'true';

    form.addEventListener('add-to-cart', () => {
      const cartSidebar = document.getElementById('site-cart-sidebar');
      if (!cartSidebar || typeof cartSidebar.show !== 'function') return;

      cartSidebar.show();

      const recommendations = document.getElementById('cart-recommendations');
      if (recommendations && typeof recommendations.generateRecommendations === 'function') {
        recommendations.generateRecommendations();
      }
    });
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    initFlourishStickyAtc();
    initFlourishProductPriceSync();
    initFlourishOpenCartOnAdd();
  });
} else {
  initFlourishStickyAtc();
  initFlourishProductPriceSync();
  initFlourishOpenCartOnAdd();
}

document.addEventListener('shopify:section:load', () => {
  initFlourishStickyAtc();
  initFlourishProductPriceSync();
  initFlourishOpenCartOnAdd();
});
